<script>
// Adapted from organization/staffCalendar/LeftPanel.vue: same layout
// (search, one dropdown filter, a colour-dotted list), but reading from the
// Real Estate `realEstate`/`realEstateAgents` stores instead of the
// Organization `branchV2`/`staffOptions` stores, with a plain <select> for
// the project filter instead of a multiselect (agents belong to several
// projects at once, so "narrow to one project" is the useful filter here,
// not "select several branches").
import { mapActions, mapGetters } from 'vuex';
import {
  loadAgentColors,
  saveAgentColors,
  getAgentColor,
  syncAgentColors,
} from './agentColorMap';
import AgentColorModal from './AgentColorModal.vue';

export default {
  name: 'CalendarLeftPanel',

  components: {
    AgentColorModal,
  },

  data() {
    return {
      search: '',
      selectedProjectId: '',
      selectedAgentId: null,
      agentColorMap: {},
      showColorModal: false,
      editingAgent: null,
    };
  },

  computed: {
    ...mapGetters('realEstateAgents', { agents: 'getAgents' }),
    ...mapGetters('realEstate', { projects: 'getProjectOptions' }),

    hasActiveFilters() {
      return (
        this.search.trim() || this.selectedProjectId || this.selectedAgentId
      );
    },
  },

  watch: {
    search() {
      this.applyFilters();
    },
    selectedProjectId() {
      this.applyFilters();
    },
    selectedAgentId() {
      this.applyFilters();
    },
    agents: {
      deep: true,
      handler() {
        this.syncColors();
      },
    },
  },

  async mounted() {
    this.agentColorMap = loadAgentColors();
    await Promise.all([this.fetchProjectOptions(), this.fetchAgents()]);
    this.syncColors();

    this.$root.$on('reAgentColorUpdated', this.reloadColors);
  },

  beforeDestroy() {
    this.$root.$off('reAgentColorUpdated', this.reloadColors);
  },

  methods: {
    ...mapActions('realEstate', ['fetchProjectOptions']),
    ...mapActions('realEstateAgents', ['fetchAgents']),

    applyFilters() {
      this.$store.dispatch('realEstateCalendar/setFilters', {
        keyword: this.search,
        propertyId: this.selectedProjectId || null,
        agentId: this.selectedAgentId,
      });
    },

    clearFilters() {
      this.search = '';
      this.selectedProjectId = '';
      this.selectedAgentId = null;
      this.$store.dispatch('realEstateCalendar/clearFilters');
    },

    reloadColors() {
      this.agentColorMap = loadAgentColors();
    },

    syncColors() {
      const activeIds = this.agents.map(a => a.agent_id);
      const next = syncAgentColors(this.agentColorMap, activeIds);
      this.agentColorMap = next;
      saveAgentColors(next);
      // AgentCalendar reads the map once on mount — often before the agents
      // (and so their palette colours) have loaded — so tell it to re-read,
      // or new agents' events stay on the grey fallback.
      this.$root.$emit('reAgentColorUpdated');
    },

    colorFor(agentId) {
      return getAgentColor(this.agentColorMap, agentId);
    },

    openColorModal(agent) {
      this.editingAgent = agent;
      this.showColorModal = true;
    },

    saveAgentColor(agentId, color) {
      const map = { ...this.agentColorMap, [String(agentId)]: color };
      this.agentColorMap = map;
      saveAgentColors(map);
      this.$root.$emit('reAgentColorUpdated');
    },
  },
};
</script>

<template>
  <aside
    class="w-72 shrink-0 h-full border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col"
  >
    <AgentColorModal
      v-if="showColorModal"
      :agent="editingAgent"
      :current-color="colorFor(editingAgent.agent_id)"
      @save="saveAgentColor"
      @close="showColorModal = false"
    />

    <div class="p-4 border-b border-slate-200 dark:border-slate-700">
      <h2 class="text-sm font-semibold text-slate-900 dark:text-slate-100">
        {{ $t('REAL_ESTATE.CALENDAR.PANEL_TITLE') }}
      </h2>
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
        {{ $t('REAL_ESTATE.CALENDAR.PANEL_DESCRIPTION') }}
      </p>

      <input
        v-model="search"
        type="text"
        :placeholder="$t('REAL_ESTATE.CALENDAR.SEARCH_PLACEHOLDER')"
        class="reset-base mt-3 mb-0 w-full text-sm px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
      />
    </div>

    <div class="flex-1 overflow-y-auto p-4 space-y-6">
      <div>
        <label
          class="text-xs font-semibold uppercase text-slate-600 dark:text-slate-400 mb-1 block"
        >
          {{ $t('REAL_ESTATE.CALENDAR.FIELD_PROJECT') }}
        </label>
        <select
          v-model="selectedProjectId"
          class="reset-base mb-0 w-full text-sm px-3 py-2 pr-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
        >
          <option value="">
            {{ $t('REAL_ESTATE.CALENDAR.ALL_PROJECTS') }}
          </option>
          <option
            v-for="project in projects"
            :key="project.property_id"
            :value="project.property_id"
          >
            {{ project.name }}
          </option>
        </select>
      </div>

      <div>
        <label
          class="text-xs font-semibold uppercase text-slate-600 dark:text-slate-400 mb-2 block"
        >
          {{ $t('REAL_ESTATE.CALENDAR.FIELD_AGENTS') }}
        </label>

        <p v-if="!agents.length" class="text-xs text-slate-400">
          {{ $t('REAL_ESTATE.CALENDAR.NO_AGENTS') }}
        </p>

        <div v-else class="space-y-1 max-h-64 overflow-auto">
          <button
            v-for="agent in agents"
            :key="agent.agent_id"
            class="w-full flex items-center gap-2 px-3 py-2 rounded text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
            :class="{
              'bg-slate-100 dark:bg-slate-800':
                selectedAgentId === agent.agent_id,
            }"
            @click="selectedAgentId = agent.agent_id"
          >
            <span
              class="w-3 h-3 rounded-full shrink-0 cursor-pointer"
              :style="{ backgroundColor: colorFor(agent.agent_id) }"
              @click.stop="openColorModal(agent)"
            />
            <span class="truncate text-slate-900 dark:text-slate-100">
              {{ agent.name }}
            </span>
          </button>

          <button
            v-if="selectedAgentId"
            class="text-xs text-woot-600 hover:underline mt-2"
            @click="selectedAgentId = null"
          >
            {{ $t('REAL_ESTATE.CALENDAR.CLEAR_AGENT_FILTER') }}
          </button>
        </div>
      </div>
    </div>

    <div class="p-4 border-t border-slate-200 dark:border-slate-700">
      <woot-button
        v-if="hasActiveFilters"
        icon="dismiss"
        color-scheme="secondary"
        class="w-full"
        @click="clearFilters"
      >
        {{ $t('REAL_ESTATE.CALENDAR.CLEAR_ALL_FILTERS') }}
      </woot-button>
    </div>
  </aside>
</template>
